import type { SaveSlot } from '../storage/SaveSlotManager';

export interface SaveSlotPanelCallbacks {
  onSave: (slotName: string) => void;
  onLoad: (slotId: string) => void;
  onDelete: (slotId: string) => void;
}

export class SaveSlotPanel {
  readonly el: HTMLDivElement;
  private callbacks: SaveSlotPanelCallbacks;
  private slotListEl: HTMLDivElement;
  private nameInput: HTMLInputElement;
  private saveBtn: HTMLButtonElement;

  constructor(container: HTMLElement, callbacks: SaveSlotPanelCallbacks) {
    this.callbacks = callbacks;

    this.el = document.createElement('div');
    this.el.className = 'save-slot-panel';

    const header = document.createElement('h3');
    header.textContent = 'Save Slots';
    this.el.appendChild(header);

    const saveRow = document.createElement('div');
    saveRow.className = 'save-row';

    this.nameInput = document.createElement('input');
    this.nameInput.type = 'text';
    this.nameInput.placeholder = 'Slot name...';
    this.nameInput.className = 'slot-name-input';
    saveRow.appendChild(this.nameInput);

    this.saveBtn = document.createElement('button');
    this.saveBtn.textContent = 'Save';
    this.saveBtn.onclick = () => {
      const name = this.nameInput.value.trim() || ('Save-' + new Date().toLocaleString());
      this.callbacks.onSave(name);
      this.nameInput.value = '';
    };
    saveRow.appendChild(this.saveBtn);

    this.el.appendChild(saveRow);

    this.slotListEl = document.createElement('div');
    this.slotListEl.className = 'slot-list';
    this.el.appendChild(this.slotListEl);

    container.appendChild(this.el);
  }

  showSlots(slots: SaveSlot[]): void {
    this.slotListEl.innerHTML = '';

    if (slots.length === 0) {
      this.slotListEl.textContent = 'No saved slots';
      return;
    }

    for (const slot of slots) {
      this.slotListEl.appendChild(this.createSlotRow(slot));
    }
  }

  private createSlotRow(slot: SaveSlot): HTMLDivElement {
    const row = document.createElement('div');
    row.className = 'slot-row';

    const infoDiv = document.createElement('div');
    infoDiv.className = 'slot-info';

    const nameEl = document.createElement('span');
    nameEl.className = 'slot-name';
    nameEl.textContent = slot.name;
    infoDiv.appendChild(nameEl);

    const timeEl = document.createElement('span');
    timeEl.className = 'slot-time';
    timeEl.textContent = new Date(slot.timestamp).toLocaleString();
    infoDiv.appendChild(timeEl);

    if (slot.screenshot && slot.screenshot.length > 0) {
      const img = document.createElement('img');
      img.className = 'slot-screenshot';
      img.src = this.uint8ArrayToDataURL(slot.screenshot);
      img.alt = 'LCD screenshot';
      infoDiv.appendChild(img);
    }

    row.appendChild(infoDiv);

    const actionsDiv = document.createElement('div');
    actionsDiv.className = 'slot-actions';

    const loadBtn = document.createElement('button');
    loadBtn.textContent = 'Load';
    loadBtn.onclick = () => this.callbacks.onLoad(slot.id);
    actionsDiv.appendChild(loadBtn);

    const deleteBtn = document.createElement('button');
    deleteBtn.textContent = 'Delete';
    deleteBtn.className = 'delete-btn';
    deleteBtn.onclick = () => {
      if (confirm('Delete save slot "' + slot.name + '"? This action cannot be undone.')) {
        this.callbacks.onDelete(slot.id);
      }
    };
    actionsDiv.appendChild(deleteBtn);

    row.appendChild(actionsDiv);

    return row;
  }

  private uint8ArrayToDataURL(data: Uint8Array): string {
    let binary = '';
    for (let i = 0; i < data.length; i++) {
      binary += String.fromCharCode(data[i]);
    }
    const base64 = btoa(binary);
    return 'data:image/png;base64,' + base64;
  }

  destroy(): void {
    if (this.el.parentNode) {
      this.el.parentNode.removeChild(this.el);
    }
  }
}
